import * as THREE from "three";
import { LevelSim, SimEventKind, isHex } from "@highmarch/domain";
import type { VFX } from "../../../../../art/vfx.js";
import { GroundMesh } from "../groundMesh";
import { roadLevelAt } from "../terrain";

/**
 * The Hex Totems' curse (TOWERS_V2, docs/TOWERS_V2.md "Hex Totem"). Each totem stands in a violet ward: a dark violet
 * band with bright rims and slowly turning runes laid over the ground at the edge of its aura (`sim.rangeOf`, gear and
 * tier included; its height sampled at every vertex, so it follows banks and bridges), a band of violet motes
 * counter-turning inside it, and a rune circle at its foot — never mistaken for the Beacon's gold ring. (A ground
 * decal the aura's size ran down the cliffs in streaks; additive mote dashes at the edge washed out to white on the
 * grass.) When an enemy comes under a totem's curse (EnemyHexed) a short violet tether flicks from the mask's eyes to it. Every enemy
 * the sim has hexed (`enemy.hexBonus > 0`) wears a small turning violet rune under its feet, with a wisp rising off
 * it now and then: on the ground, so it never sits on the frost crust, the flames or the poison drips the other
 * statuses draw on the body. Reduced effects keep the ward and the runes, still, without the motes, wisps and tether.
 */

/** The ward's look: its violet, the inner mote band (VFX auraRing; alpha by tier) and the rune circle at its foot. */
export const HEX_WARD = {
  color: [0.72, 0.36, 1.0] as [number, number, number],
  /** The inner band's radius (× the aura), the rune circle at the totem's foot (world size) and its turn rate. */
  inner: 0.62,
  footRune: 1.15,
  runeSpin: 0.25,
  /** The inner band's dash spacing (world units): short, since a long flat dash over a bank or a chasm edge read as a
   * violet laser line across the map. */
  dash: 0.26,
  alpha: [0.75, 0.85, 1.0] as readonly number[],
  size: 0.2,
  thickness: 0.07,
  spin: -0.1,
} as const;

/** The mark under a hexed enemy: its size (× the enemy's ring), turn rate, fade, and seconds between wisps. */
export const HEX_MARK = { size: 0.62, spin: 1.3, fadeIn: 0.18, fadeOut: 0.3, wispEvery: 0.45 } as const;


/** A mark's opacity a frame on: towards 1 while hexed, towards 0 after. Pure. */
export function markFade(shown: number, hexed: boolean, dt: number): number {
  return hexed ? Math.min(1, shown + dt / HEX_MARK.fadeIn) : Math.max(0, shown - dt / HEX_MARK.fadeOut);
}

interface Ward { seen: boolean; ring: THREE.Mesh | null; radius: number; x: number; z: number }

/** Segments round a ward ring, and its width (world units) over the ground. */
const WARD_SEGS = 128;
export const WARD_RING = { width: 0.17, lift: 0.04, runes: 18 } as const;

const RING_VERT = /* glsl */ `
attribute vec2 aRing;   // u: round the ring (0..1), v: across it (0 inner, 1 outer)
varying vec2 vRing;
void main() { vRing = aRing; gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0); }`;
const RING_FRAG = /* glsl */ `
uniform float uTime;
uniform float uAlpha;
uniform float uRunes;
varying vec2 vRing;
void main() {
  float v = vRing.y, u = vRing.x;
  // a dark violet band with a bright rim line on each edge, and runes turning slowly along its middle
  float rims = exp(-pow((v - 0.08) / 0.07, 2.0)) + exp(-pow((v - 0.92) / 0.07, 2.0));
  float k = fract(u * uRunes - uTime * 0.05);
  float rune = step(0.18, k) * step(k, 0.62) * (step(0.35, v) * step(v, 0.65) + step(abs(k - 0.4), 0.04) * step(0.25, v) * step(v, 0.75));
  float band = smoothstep(0.0, 0.08, v) * smoothstep(1.0, 0.92, v);
  vec3 deep = vec3(0.16, 0.04, 0.3);
  vec3 glow = vec3(0.85, 0.42, 1.6);
  vec3 c = mix(deep, glow, clamp(rims * 0.9 + rune * 0.85, 0.0, 1.0));
  float a = band * (0.28 + 0.52 * clamp(rims + rune, 0.0, 1.0)) * uAlpha;
  gl_FragColor = vec4(c, a);
}`;

/** A flat ring of `radius` round (x, z), laid over the ground as drawn (its height sampled at every vertex). */
export function wardRingGeometry(x: number, z: number, radius: number, heightAt: (x: number, z: number) => number): THREE.BufferGeometry {
  const pos: number[] = [], ring: number[] = [], idx: number[] = [];
  const r0 = Math.max(0.05, radius - WARD_RING.width / 2), r1 = radius + WARD_RING.width / 2;
  for (let i = 0; i <= WARD_SEGS; i++) {
    const a = (i / WARD_SEGS) * Math.PI * 2, c = Math.cos(a), sn = Math.sin(a);
    for (const [r, v] of [[r0, 0], [r1, 1]] as const) {
      const px = x + c * r, pz = z + sn * r;
      pos.push(px, heightAt(px, pz) + WARD_RING.lift, pz);
      ring.push(i / WARD_SEGS, v);
    }
    if (i < WARD_SEGS) { const b = i * 2; idx.push(b, b + 2, b + 1, b + 1, b + 2, b + 3); }
  }
  const g = new THREE.BufferGeometry();
  g.setAttribute("position", new THREE.Float32BufferAttribute(pos, 3));
  g.setAttribute("aRing", new THREE.Float32BufferAttribute(ring, 2));
  g.setIndex(idx);
  return g;
}
interface Mark { shown: number; wispIn: number; seed: number; seen: boolean }

const VERT = /* glsl */ `
attribute vec4 aPos;   // x, y, z, size
attribute vec2 aPrm;   // alpha, rotation
varying vec2 vP;
varying float vA;
void main() {
  float c = cos(aPrm.y), s = sin(aPrm.y);
  vec2 q = position.xy;
  vP = vec2(c * q.x - s * q.y, s * q.x + c * q.y) * 2.0;
  vA = aPrm.x;
  vec3 w = vec3(aPos.x + position.x * aPos.w, aPos.y + 0.035, aPos.z - position.y * aPos.w);
  gl_Position = projectionMatrix * viewMatrix * vec4(w, 1.0);
}`;
const FRAG = /* glsl */ `
varying vec2 vP;
varying float vA;
void main() {
  float r = length(vP);
  if (r > 1.0) discard;
  float a = atan(vP.y, vP.x);
  float ring = exp(-pow((r - 0.82) / 0.07, 2.0));
  float inner = exp(-pow((r - 0.55) / 0.05, 2.0)) * 0.7;
  // five glyph ticks between the rings, and an eye at the centre
  float glyph = step(0.82, abs(cos(a * 2.5))) * smoothstep(0.58, 0.62, r) * smoothstep(0.78, 0.74, r);
  float eye = smoothstep(0.2, 0.12, length(vec2(vP.x, vP.y * 1.9))) * (1.0 - smoothstep(0.03, 0.07, r)) + smoothstep(0.2, 0.12, length(vec2(vP.x, vP.y * 1.9))) * 0.45;
  float m = clamp(ring + inner + glyph * 0.9 + eye, 0.0, 1.0);
  // alpha-blended, not added: a deep violet with glowing (HDR) rings reads as violet on grass, sand and stone alike
  vec3 col = mix(vec3(0.32, 0.07, 0.55), vec3(1.15, 0.55, 1.6), clamp(ring * 0.7 + eye * 0.7, 0.0, 1.0));
  float alpha = (m * 0.9 + 0.18 * smoothstep(0.9, 0.2, r)) * vA;
  gl_FragColor = vec4(col, clamp(alpha, 0.0, 1.0));
}`;

const _p = new THREE.Vector3(), _q = new THREE.Vector3();

export class HexWards {
  readonly group = new THREE.Group();
  private readonly wards = new Map<number, Ward>();
  private readonly marks = new Map<number, Mark>();
  private readonly geo = new THREE.InstancedBufferGeometry();
  private readonly pos: Float32Array;
  private readonly prm: Float32Array;
  private readonly mesh: THREE.Mesh;
  private time = 0;
  static readonly MaxMarks = 48;
  private readonly ringMat = new THREE.ShaderMaterial({ vertexShader: RING_VERT, fragmentShader: RING_FRAG, uniforms: { uTime: { value: 0 }, uAlpha: { value: 1 }, uRunes: { value: WARD_RING.runes } }, transparent: true, depthWrite: false, side: THREE.DoubleSide, polygonOffset: true, polygonOffsetFactor: -2, polygonOffsetUnits: -4 });
  /** Set by the owner: an enemy's drawn feet (EnemyViews.feet) and body middle, and a tower part's world position. */
  enemyFeet: ((enemyId: number) => THREE.Vector3 | null) | null = null;
  enemyBody: ((enemyId: number) => THREE.Vector3 | null) | null = null;
  towerNode: ((towerId: number, part: string) => THREE.Vector3 | null) | null = null;
  /** Set by the owner: a totem has cursed someone new this tick (its `fire` pulse and the curse sound). */
  onCurse: ((towerId: number) => void) | null = null;

  constructor(private readonly sim: LevelSim, private readonly fx: () => VFX | null, private readonly reduced: () => boolean) {
    const quad = new THREE.PlaneGeometry(1, 1);
    this.geo.index = quad.index;
    this.geo.setAttribute("position", quad.getAttribute("position"));
    this.pos = new Float32Array(HexWards.MaxMarks * 4); this.prm = new Float32Array(HexWards.MaxMarks * 2);
    this.geo.setAttribute("aPos", new THREE.InstancedBufferAttribute(this.pos, 4).setUsage(THREE.DynamicDrawUsage));
    this.geo.setAttribute("aPrm", new THREE.InstancedBufferAttribute(this.prm, 2).setUsage(THREE.DynamicDrawUsage));
    this.geo.instanceCount = 0;
    this.mesh = new THREE.Mesh(this.geo, new THREE.ShaderMaterial({ vertexShader: VERT, fragmentShader: FRAG, transparent: true, depthWrite: false }));
    this.mesh.frustumCulled = false;
    this.mesh.renderOrder = 6;
    this.group.add(this.mesh);
  }

  onTick(): void {
    const sim = this.sim;
    const fx = this.reduced() ? null : this.fx();
    const cursed = new Set<number>();
    for (const ev of sim.events.all()) {
      if (ev.kind !== SimEventKind.EnemyHexed) continue;
      cursed.add(ev.b);
      if (!fx) continue;
      // the curse leaves the mask's eyes for its new victim: a short violet tether and a snap of runes at its feet
      const eye = this.towerNode?.(ev.b, "eye0") ?? this.towerNode?.(ev.b, "mask");
      const body = this.enemyBody?.(ev.a);
      if (eye && body) fx.lightning(eye, body, { life: 0.22, width: 0.05, glow: 0.16, core: 0.016, coreBright: 2.2, jag: 0.5, color: 0xb45cff, ambient: true });
      const feet = this.enemyFeet?.(ev.a);
      if (feet) fx.ring(_p.copy(feet).setY(GroundMesh.drawnAt(feet.x, feet.z) + 0.04), { size: 0.9, life: 0.35, color: HEX_WARD.color, flat: true, bright: 1.6 });
    }
    for (const id of cursed) this.onCurse?.(id);
  }

  update(dt: number): void {
    this.time += dt;
    const sim = this.sim, reduced = this.reduced(), fx = this.fx();
    this.ringMat.uniforms.uTime.value = reduced ? 0 : this.time;
    // the wards
    let n = 0;
    for (const w of this.wards.values()) w.seen = false;
    for (const t of sim.towerSlots) {
      if (!t.alive || !isHex(sim.balance.towers[t.archetype])) continue;
      const radius = sim.rangeOf(t).toNumber();
      let w = this.wards.get(t.id);
      if (!w) { w = { seen: true, ring: null, radius: 0, x: NaN, z: NaN }; this.wards.set(t.id, w); }
      w.seen = true;
      const at = _p.set(t.cell.x, GroundMesh.drawnAt(t.cell.x, t.cell.y), t.cell.y);
      // the ward's edge: a violet band laid over the ground at the aura's radius (rebuilt when the radius changes)
      if (!w.ring || w.radius !== radius || w.x !== at.x || w.z !== at.z) {
        if (w.ring) { this.group.remove(w.ring); w.ring.geometry.dispose(); }
        w.ring = new THREE.Mesh(wardRingGeometry(at.x, at.z, radius, (x, z) => Math.max(GroundMesh.drawnAt(x, z), roadLevelAt(x, z))), this.ringMat);
        w.ring.renderOrder = 5; w.ring.frustumCulled = false;
        this.group.add(w.ring);
        w.radius = radius; w.x = at.x; w.z = at.z;
      }
      w.ring.visible = true;
      if (fx) {
        const disabled = t.disabledTicksLeft > 0;
        const tier = Math.max(1, Math.min(3, t.tier));
        const alpha = HEX_WARD.alpha[tier - 1] * (disabled ? 0.3 : 1) * (reduced ? 1 : 0.85 + 0.15 * Math.sin(this.time * 2.2 + t.id));
        // a fainter inner band turning the other way, so the ward reads as a circle of runes, not the Beacon's ring
        fx.auraRing(at, radius * HEX_WARD.inner, roadLevelAt, {
          count: Math.round((2 * Math.PI * radius * HEX_WARD.inner) / HEX_WARD.dash), time: this.time, spin: reduced ? 0 : -HEX_WARD.spin * 1.6,
          alpha: alpha * 0.45, size: HEX_WARD.size * 0.6, thickness: HEX_WARD.thickness * 0.7, color: HEX_WARD.color,
        });
      }
      // the rune circle at the totem's foot (the marks layer, flat): its own ward, turning slowly
      if (n < HexWards.MaxMarks) {
        this.pos.set([at.x, at.y + 0.005, at.z, HEX_WARD.footRune], n * 4);
        this.prm.set([0.75, reduced ? 0 : -this.time * HEX_WARD.runeSpin], n * 2);
        n++;
      }
    }
    for (const [id, w] of this.wards) if (!w.seen) { if (w.ring) { this.group.remove(w.ring); w.ring.geometry.dispose(); } this.wards.delete(id); }
    // the marks
    for (const m of this.marks.values()) m.seen = false;
    for (const e of sim.enemySlots) {
      const hexed = e.alive && e.hexBonus.isPositive();
      let m = this.marks.get(e.id);
      if (!m) { if (!hexed) continue; m = { shown: 0, wispIn: Math.random() * HEX_MARK.wispEvery, seed: Math.random() * 6.3, seen: true }; this.marks.set(e.id, m); }
      m.seen = true;
      m.shown = markFade(m.shown, hexed, dt);
      if (m.shown <= 0) { this.marks.delete(e.id); continue; }
      const feet = this.enemyFeet?.(e.id);
      if (!feet || n >= HexWards.MaxMarks) continue;
      const y = GroundMesh.drawnAt(feet.x, feet.z);
      const size = HEX_MARK.size * (e.isBoss ? 2 : 1);
      this.pos.set([feet.x, y, feet.z, size], n * 4);
      this.prm.set([m.shown, reduced ? m.seed : m.seed + this.time * HEX_MARK.spin], n * 2);
      n++;
      if (!reduced && fx && hexed && (m.wispIn -= dt) <= 0) {
        m.wispIn = HEX_MARK.wispEvery * (0.7 + Math.random() * 0.6);
        fx.sparkles(_q.set(feet.x, y + 0.08, feet.z), 1, { color: HEX_WARD.color, radius: 0.16, rise: 0.7, life: 0.7, size: 0.08 });
      }
    }
    for (const [id, m] of this.marks) if (!m.seen) this.marks.delete(id);
    this.geo.instanceCount = n;
    this.mesh.visible = n > 0;
    if (n > 0) { this.geo.attributes.aPos.needsUpdate = true; this.geo.attributes.aPrm.needsUpdate = true; }
  }

  /** Enemies wearing a mark this frame (tests, captures). */
  get marked(): number { return this.marks.size; }

  dispose(): void {
    for (const w of this.wards.values()) w.ring?.geometry.dispose();
    this.wards.clear(); this.marks.clear();
    this.geo.dispose(); (this.mesh.material as THREE.Material).dispose(); this.ringMat.dispose();
    this.group.clear();
  }
}
